/**
 * The WebXR `immersive-ar` surface Babylon's WebXR stack and `XRManager`
 * use, backed by a native host (`appclip/protocol/PROTOCOL.md`). Members
 * nothing here calls are left out on purpose; add one only with the caller
 * that needs it.
 */

import { withTimeout, type FrameMessage, type HelloReply, type ImageScore, type Mat4, type NativeHost, type NativeMessage, type RequestSessionReply } from './protocol';
import { IDENTITY, RigidTransform, invertRigid, multiply, screenRay, withDepthRange } from './rigid';
import { presentOverCamera } from './present';

type WorldFrom = (frame: FrameMessage) => Mat4 | null;
type FrameCallback = (time: number, frame: NativeXRFrame) => void;

const SUPPORTED_FEATURES = ['viewer', 'local', 'unbounded', 'image-tracking', 'dom-overlay'];
/** Names a native refusal may carry; anything else surfaces as `OperationError`. */
const NATIVE_ERRORS = ['NotAllowedError', 'NotSupportedError'];
/** Leaving AR never waits on native longer than this. */
const END_TIMEOUT_MS = 1000;
/** A session whose frames stop for this long has ended (PROTOCOL.md). */
const FRAME_TIMEOUT_MS = 5000;

const isImageScore = (s: unknown): s is ImageScore => s === 'trackable' || s === 'untrackable';
const isMatrix = (m: unknown): boolean => Array.isArray(m) && m.length === 16 && m.every(Number.isFinite);
function isWellFormed(msg: FrameMessage): boolean {
    return isMatrix(msg.view?.transform) && isMatrix(msg.view?.projection)
        && Array.isArray(msg.images) && msg.images.every(image => isMatrix(image.transform));
}

function event<T extends object>(type: string, init: T): Event & T {
    return Object.assign(new Event(type), init);
}

export class NativeXRSpace extends EventTarget {
    constructor(readonly _worldFrom: WorldFrom) { super(); }
}

export class NativeXRReferenceSpace extends NativeXRSpace {
    getOffsetReferenceSpace(originOffset: RigidTransform): NativeXRReferenceSpace {
        const parent = this._worldFrom;
        const offset = Array.from(originOffset.matrix);
        return new NativeXRReferenceSpace(frame => {
            const worldFromParent = parent(frame);
            return worldFromParent && multiply(worldFromParent, offset);
        });
    }
}

export class NativeXRInputSource {
    readonly handedness = 'none';
    readonly targetRayMode = 'transient-pointer';
    readonly targetRaySpace: NativeXRSpace;
    ndc: { x: number; y: number };

    constructor(ndc: { x: number; y: number }) {
        this.ndc = ndc;
        this.targetRaySpace = new NativeXRSpace(f => screenRay(f.view.transform, f.view.projection, this.ndc.x, this.ndc.y));
    }
}

export class NativeXRFrame {
    active = true;

    constructor(readonly session: NativeXRSession, private readonly _msg: FrameMessage) {}

    getPose(space: NativeXRSpace, baseSpace: NativeXRSpace): { transform: RigidTransform; emulatedPosition: boolean } | null {
        if (!this.active) throw new DOMException('The frame is no longer active.', 'InvalidStateError');
        const worldFromSpace = space._worldFrom(this._msg);
        const worldFromBase = baseSpace._worldFrom(this._msg);
        if (!worldFromSpace || !worldFromBase) return null;
        return {
            transform: RigidTransform.fromMatrix(multiply(invertRigid(worldFromBase), worldFromSpace)),
            emulatedPosition: this._msg.tracking !== 'normal',
        };
    }

    getViewerPose(referenceSpace: NativeXRReferenceSpace) {
        if (this._msg.tracking === 'none') return null;
        const pose = this.getPose(this.session._viewerSpace, referenceSpace);
        if (!pose) return null;
        const { depthNear, depthFar } = this.session.renderState;
        const view = {
            eye: 'none' as const,
            projectionMatrix: withDepthRange(this._msg.view.projection, depthNear, depthFar),
            transform: pose.transform,
        };
        return { ...pose, views: [view] };
    }

    getImageTrackingResults() {
        return this._msg.images.map(image => ({
            index: image.index,
            imageSpace: this.session._imageSpace(image.index),
            trackingState: image.state,
            measuredWidthInMeters: image.widthM,
        }));
    }
}

/** `XRWebGLLayer` over the canvas's default framebuffer, which is what native composites. */
export class NativeXRWebGLLayer {
    readonly framebuffer = null;

    constructor(session: NativeXRSession, readonly context: WebGLRenderingContext | WebGL2RenderingContext) {
        if (session._ended) throw new DOMException('The session has ended.', 'InvalidStateError');
    }

    get framebufferWidth(): number { return this.context.drawingBufferWidth; }
    get framebufferHeight(): number { return this.context.drawingBufferHeight; }

    getViewport(_view: unknown) {
        return { x: 0, y: 0, width: this.framebufferWidth, height: this.framebufferHeight };
    }
}

interface Touch { source: NativeXRInputSource; bornTick: number }
type TouchOp = { kind: 'down'; id: number; ndc: { x: number; y: number } } | { kind: 'up'; id: number; cancelled: boolean };

export class NativeXRSession extends EventTarget {
    readonly domOverlayState: { type: 'screen' } | undefined;
    readonly renderState: { baseLayer: NativeXRWebGLLayer | null; depthNear: number; depthFar: number } =
        { baseLayer: null, depthNear: 0.1, depthFar: 1000 };
    _ended = false;

    readonly _viewerSpace = new NativeXRSpace(f => f.view.transform);
    private readonly _imageSpaces = new Map<number, NativeXRSpace>();
    /** Spaces that receive `reset`: the ones the page requested, not their offsets. */
    private readonly _resettable: NativeXRReferenceSpace[] = [];
    private _callbacks = new Map<number, FrameCallback>();
    private _nextHandle = 1;
    private _raf = 0;
    private _latest: FrameMessage | null = null;
    private _drawn: FrameMessage | null = null;
    private _tickCount = 0;
    private _pending: TouchOp[] = [];
    private readonly _touches = new Map<number, Touch>();
    private _restorePage: (() => void) | null = null;
    private _ending: Promise<void> | null = null;
    private _watchdog = 0;
    private _warnedMalformed = false;

    constructor(
        private readonly _host: NativeHost,
        readonly enabledFeatures: readonly string[],
        private readonly _imageScores: ImageScore[],
        private readonly _overlayRoot: HTMLElement | null,
        private readonly _onEnd: () => void,
    ) {
        super();
        this.domOverlayState = _overlayRoot ? { type: 'screen' } : undefined;
        this._armWatchdog();
        document.addEventListener('pointerdown', this._onPointer, true);
        document.addEventListener('pointermove', this._onPointer, true);
        document.addEventListener('pointerup', this._onPointer, true);
        document.addEventListener('pointercancel', this._onPointer, true);
    }

    updateRenderState(state: Partial<NativeXRSession['renderState']>): void {
        if (this._ended) throw new DOMException('The session has ended.', 'InvalidStateError');
        for (const [key, value] of Object.entries(state)) {
            if (value !== undefined) Object.assign(this.renderState, { [key]: value });
        }
        const canvas = this.renderState.baseLayer?.context.canvas;
        if (canvas instanceof HTMLCanvasElement && !this._restorePage) this._restorePage = presentOverCamera(canvas, this._overlayRoot);
    }

    async requestReferenceSpace(type: string): Promise<NativeXRReferenceSpace> {
        if (type === 'viewer') return new NativeXRReferenceSpace(f => f.view.transform);
        if ((type === 'local' || type === 'unbounded') && this.enabledFeatures.includes(type)) {
            const space = new NativeXRReferenceSpace(() => IDENTITY);
            this._resettable.push(space);
            return space;
        }
        throw new DOMException(`Reference space "${type}" is not supported.`, 'NotSupportedError');
    }

    requestAnimationFrame(callback: FrameCallback): number {
        if (this._ended) return 0;
        const handle = this._nextHandle++;
        this._callbacks.set(handle, callback);
        this._schedule();
        return handle;
    }

    cancelAnimationFrame(handle: number): void {
        this._callbacks.delete(handle);
    }

    async getTrackedImageScores(): Promise<ImageScore[]> {
        return [...this._imageScores];
    }

    end(): Promise<void> {
        if (this._ended) return Promise.resolve();
        // A synchronous throw from the transport still ends the session.
        this._ending ??= withTimeout(Promise.resolve().then(() => this._host.request({ type: 'endSession' })), END_TIMEOUT_MS)
            .catch(() => {})
            .then(() => this._shutdown());
        return this._ending;
    }

    _imageSpace(index: number): NativeXRSpace {
        let space = this._imageSpaces.get(index);
        if (!space) {
            space = new NativeXRSpace(f => f.images.find(image => image.index === index)?.transform ?? null);
            this._imageSpaces.set(index, space);
        }
        return space;
    }

    _receive(msg: NativeMessage): void {
        if (this._ended) return;
        switch (msg.type) {
            case 'frame':
                if (!isWellFormed(msg)) {
                    if (!this._warnedMalformed) console.warn('[nativeXR] dropping a frame whose matrices are not 16 finite numbers', msg);
                    this._warnedMalformed = true;
                    break;
                }
                this._latest = msg;
                this._armWatchdog();
                this._schedule();
                break;
            case 'reset':
                // A frame from before the reset is in the old world: never draw it.
                this._latest = null;
                for (const space of this._resettable) space.dispatchEvent(event('reset', { referenceSpace: space, transform: null }));
                break;
            case 'ended':
                this._shutdown();
                break;
        }
    }

    private _armWatchdog(): void {
        window.clearTimeout(this._watchdog);
        this._watchdog = window.setTimeout(() => void this.end(), FRAME_TIMEOUT_MS);
    }

    /** A tick runs the callbacks only for a native frame not drawn yet; `_receive` schedules the next. */
    private _schedule(): void {
        if (this._raf || this._ended || !this._callbacks.size || !this._latest || this._latest === this._drawn) return;
        this._raf = window.requestAnimationFrame(this._tick);
    }

    private readonly _tick = (time: number): void => {
        this._raf = 0;
        const msg = this._latest;
        if (this._ended || !msg || msg === this._drawn) return;
        this._drawn = msg;
        this._tickCount++;
        const frame = new NativeXRFrame(this, msg);
        this._processInput(frame);
        const callbacks = this._callbacks;
        this._callbacks = new Map();
        for (const callback of callbacks.values()) {
            try { callback(time, frame); } catch (error) { reportError(error); }
        }
        frame.active = false;
        this._host.ack({ type: 'rendered', t: msg.t });
    };

    private readonly _onPointer = (e: PointerEvent): void => {
        // The view, and so the projection, spans the window: the canvas is full-screen in AR.
        const ndc = { x: 2 * e.clientX / window.innerWidth - 1, y: 1 - 2 * e.clientY / window.innerHeight };
        if (e.type === 'pointermove') {
            const touch = this._touches.get(e.pointerId);
            if (touch) touch.source.ndc = ndc;
            for (const op of this._pending) if (op.kind === 'down' && op.id === e.pointerId) op.ndc = ndc;
            return;
        }
        if (e.type === 'pointerdown') {
            const before = new Event('beforexrselect', { bubbles: true, cancelable: true });
            e.target?.dispatchEvent(before);
            if (!before.defaultPrevented) this._pending.push({ kind: 'down', id: e.pointerId, ndc });
            return;
        }
        this._pending.push({ kind: 'up', id: e.pointerId, cancelled: e.type === 'pointercancel' });
    };

    /**
     * Touches become transient-pointer input at a frame boundary. A touch
     * lifted in the tick it began ends one tick later, so every tap is seen
     * by at least one frame.
     */
    private _processInput(frame: NativeXRFrame): void {
        const ops = this._pending;
        this._pending = [];
        for (const op of ops) {
            if (op.kind === 'down') {
                const source = new NativeXRInputSource(op.ndc);
                this._touches.set(op.id, { source, bornTick: this._tickCount });
                this.dispatchEvent(event('inputsourceschange', { session: this, added: [source], removed: [] }));
                this.dispatchEvent(event('selectstart', { frame, inputSource: source }));
                continue;
            }
            const touch = this._touches.get(op.id);
            if (!touch) continue;
            if (touch.bornTick === this._tickCount) { this._pending.push(op); continue; }
            this._touches.delete(op.id);
            if (!op.cancelled) this.dispatchEvent(event('select', { frame, inputSource: touch.source }));
            this.dispatchEvent(event('selectend', { frame, inputSource: touch.source }));
            this.dispatchEvent(event('inputsourceschange', { session: this, added: [], removed: [touch.source] }));
        }
    }

    private _shutdown(): void {
        if (this._ended) return;
        this._ended = true;
        window.clearTimeout(this._watchdog);
        if (this._raf) window.cancelAnimationFrame(this._raf);
        this._callbacks.clear();
        this._pending = [];
        this._touches.clear();
        for (const type of ['pointerdown', 'pointermove', 'pointerup', 'pointercancel']) {
            document.removeEventListener(type, this._onPointer as EventListener, true);
        }
        this._restorePage?.();
        this._restorePage = null;
        this._onEnd();
        this.dispatchEvent(event('end', { session: this }));
    }
}

function pngBase64(image: ImageBitmap): string {
    const canvas = document.createElement('canvas');
    canvas.width = image.width;
    canvas.height = image.height;
    canvas.getContext('2d')!.drawImage(image, 0, 0);
    return canvas.toDataURL('image/png').slice('data:image/png;base64,'.length);
}

interface SessionInit {
    requiredFeatures?: string[];
    optionalFeatures?: string[];
    trackedImages?: { image: ImageBitmap; widthInMeters: number }[];
    domOverlay?: { root: HTMLElement };
}

/** `navigator.xr`. */
export class NativeXRSystem {
    /** Set from the request until the session ends, so only one runs. */
    private _active: NativeXRSession | 'starting' | null = null;

    constructor(private readonly _host: NativeHost, private readonly _hello: HelloReply) {}

    async isSessionSupported(mode: string): Promise<boolean> {
        return mode === 'immersive-ar' && this._hello.immersiveAR === true;
    }

    async requestSession(mode: string, init: SessionInit = {}): Promise<NativeXRSession> {
        if (mode !== 'immersive-ar' || this._hello.immersiveAR !== true) throw new DOMException(`Session mode "${mode}" is not supported.`, 'NotSupportedError');
        if (this._active) throw new DOMException('An immersive session is already active.', 'InvalidStateError');
        this._active = 'starting';
        try {
            const session = await this._start(init);
            this._active = session;
            return session;
        } catch (error) {
            this._active = null;
            throw error;
        }
    }

    private async _start(init: SessionInit): Promise<NativeXRSession> {
        const missing = (init.requiredFeatures ?? []).find(f => !SUPPORTED_FEATURES.includes(f));
        if (missing) throw new DOMException(`Feature "${missing}" is not supported.`, 'NotSupportedError');
        const requested = [...(init.requiredFeatures ?? []), ...(init.optionalFeatures ?? [])];
        const enabled = SUPPORTED_FEATURES.filter(f => f === 'viewer' || f === 'local' || requested.includes(f));
        const images = enabled.includes('image-tracking') ? init.trackedImages ?? [] : [];
        let reply: RequestSessionReply;
        try {
            reply = await this._host.request({
                type: 'requestSession',
                images: images.map(i => ({ png: pngBase64(i.image), widthM: i.widthInMeters })),
            }) as RequestSessionReply;
        } catch (error) {
            const name = error instanceof Error ? error.message : String(error);
            throw new DOMException('The native host refused the session.', NATIVE_ERRORS.includes(name) ? name : 'OperationError');
        }
        const scores: unknown = (reply as Partial<RequestSessionReply> | null)?.imageScores;
        if (!Array.isArray(scores) || scores.length !== images.length || !scores.every(isImageScore)) {
            // Native started a session the page can't use: release it.
            await withTimeout(Promise.resolve().then(() => this._host.request({ type: 'endSession' })), END_TIMEOUT_MS).catch(() => {});
            throw new DOMException('The native host sent an invalid session reply.', 'OperationError');
        }
        const overlayRoot = enabled.includes('dom-overlay') ? init.domOverlay?.root ?? null : null;
        return new NativeXRSession(this._host, enabled, scores, overlayRoot, () => { this._active = null; });
    }

    _receive(msg: NativeMessage): void {
        if (this._active instanceof NativeXRSession) this._active._receive(msg);
    }
}
